import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getPaymentReturnContext,
  downloadReceipt,
} from "../../api/paymentService";
import { getJourneyData } from "../../api/journeyService";
import PaymentModal from "../../common/components/PaymentModal";
import { logError } from "../../common/utils/logger";
import BrandLoader from "../../common/components/BrandLoader";
import { REINITIATE_PAYMENT_SUMMARY_SESSION_KEY } from "../../constants";
import {
  decryptJourneyQueryValue,
  encryptJourneyQueryValue,
} from "../../common/utils/journeyEncryption";
import { getSafeRedirectUrl } from "../../common/utils/safeNavigation";
import { resolveContinueJourneyPage } from "../../common/utils/continueJourneyPage";

/**
 * Align with payment-return context API: `transactionStatus` and/or top-level `status` (e.g. SUCCESS / FAILED).
 */
const normalizeGatewayStatus = (raw) => {
  const s = String(raw ?? "").trim().toUpperCase();
  if (
    ["SUCCESS", "COMPLETED", "CAPTURED", "PAID", "SUCCESSFUL", "SUCCEEDED"].includes(
      s,
    )
  ) {
    return "SUCCESS";
  }
  if (["FAILED", "FAILURE", "REJECTED", "DECLINED", "CANCELLED", "CANCELED"].includes(s)) {
    return "FAILED";
  }
  return s;
};

const firstNonEmptyString = (...candidates) => {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s !== "") return s;
  }
  return "";
};

const normalizeAmount = (value) => {
  if (value == null) return null;
  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
};

const resolvePaidAmount = (context, urlAmount) => {
  // Prefer backend context over URL amount (gateway URL may omit/alter amount).
  const candidates = [
    context?.paidAmount,
    context?.paymentAmount,
    context?.amount,
    context?.transactionAmount,
    context?.totalAmount,
    context?.premiumAmount,
    context?.transaction?.amount,
    context?.transaction?.paymentAmount,
    context?.payment?.amount,
    context?.payment?.premiumAmount,
    context?.paymentData?.premiumAmount,
    context?.paymentData?.amount,
    context?.quoteSummaryData?.totalPayableAmount,
    context?.quoteSummaryData?.premiumAmount,
    urlAmount,
  ];
  const normalized = candidates
    .map((candidate) => normalizeAmount(candidate))
    .filter((value) => value != null);

  // Real paid amount should be positive; skip zero placeholders from gateway/context.
  const positive = normalized.find((value) => value > 0);
  if (positive != null) return positive;

  // Fallback only if we genuinely have no positive amount.
  const zeroOrNegative = normalized.find((value) => value === 0 || value < 0);
  if (zeroOrNegative != null) return zeroOrNegative;

  return null;
};

const resolvePaidAmountFromJourney = (journey) => {
  if (!journey || typeof journey !== "object") return null;
  const stages = journey?.stages && typeof journey.stages === "object" ? journey.stages : {};
  const currentStageData =
    journey?.currentStageData && typeof journey.currentStageData === "object"
      ? journey.currentStageData
      : {};
  const merged = { ...stages, ...currentStageData };
  const allStages = Object.values(merged).filter((v) => v && typeof v === "object");

  const candidates = [];
  allStages.forEach((stage) => {
    candidates.push(
      stage?.paymentData?.premiumAmount,
      stage?.quoteSummaryData?.totalPayableAmount,
      stage?.summaryData?.totalPayableAmount,
      stage?.premiumAmount,
    );
  });

  for (const candidate of candidates) {
    const normalized = normalizeAmount(candidate);
    if (normalized != null && normalized > 0) return normalized;
  }
  return null;
};

const hasVerifiedPaymentReturnSignature = (context) => {
  if (!context || typeof context !== "object") return false;
  return (
    context.signatureVerified === true ||
    context.hashVerified === true ||
    context.paymentReturnVerified === true ||
    context.returnSignatureVerified === true
  );
};

const PaymentReturn = () => {
  const [searchParams] = useSearchParams();
  // Gateway sends applicationNo (not applicationNumber); support both
  const applicationNumberRaw = (
    searchParams.get("applicationNumber") ||
    searchParams.get("applicationNo") ||
    ""
  ).trim();
  const applicationNumber =
    applicationNumberRaw
      ? decryptJourneyQueryValue(applicationNumberRaw) || applicationNumberRaw
      : "";
  const productId = searchParams.get("productId") || "";
  const status = (searchParams.get("status") || "").toUpperCase();
  const amount = searchParams.get("amount") || "";
  const transactionId =
    searchParams.get("transactionId") ||
    searchParams.get("transactionNo") ||
    searchParams.get("txnId") ||
    searchParams.get("txn_id") ||
    "";
  const redirectionKey =
    searchParams.get("key") ||
    searchParams.get("redirectionKey") ||
    searchParams.get("transactionRedirectionKey") ||
    "";
  const orderId =
    searchParams.get("orderId") ||
    searchParams.get("receipt") ||
    searchParams.get("sAddInfo2") ||
    "";

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receiptDownloading, setReceiptDownloading] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [journeyPaidAmount, setJourneyPaidAmount] = useState(null);

  const resolvedTransactionStatus = normalizeGatewayStatus(
    firstNonEmptyString(
      context?.transactionStatus,
      context?.status,
      status,
    ),
  );
  const isSuccess = resolvedTransactionStatus === "SUCCESS";

  useEffect(() => {
    if (!applicationNumber.trim()) {
      setError("Application number is required.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const returnContext = await getPaymentReturnContext({
          applicationNumber,
          productId,
          status,
          amount,
          transactionId,
          redirectionKey,
          orderId,
        });
        if (cancelled) return;
        if (!hasVerifiedPaymentReturnSignature(returnContext)) {
          throw new Error(
            "Payment return validation failed. Please retry from the official payment flow.",
          );
        }
        setContext(returnContext);

        // Read journey only after returnContext finishes, so backend payment status
        // updates are visible on the very first post-payment load.
        const journey = await getJourneyData(applicationNumber.trim());
        if (cancelled) return;
        const paid = resolvePaidAmountFromJourney(journey);
        if (paid != null) setJourneyPaidAmount(paid);
      } catch (e) {
        if (cancelled) return;
        logError("[PaymentReturn] Failed to load payment return details", e);
        setError(e.message || "Failed to load payment return details.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applicationNumber, productId, status, amount, transactionId, redirectionKey, orderId]);

  const handleDownloadReceipt = useCallback(async () => {   
    if (!applicationNumber?.trim() || !context?.transactionId?.trim()) return;
    setReceiptError(null);
    setReceiptDownloading(true);
    try {
      await downloadReceipt({
        applicationNumber: applicationNumber.trim(),
        transactionId: context.transactionId.trim(),
      });
    } catch (e) {
      logError("[PaymentReturn] Receipt download failed", e);
      setReceiptError(
        e?.message || "Failed to download receipt. Please try again.",
      );
    } finally {
      setReceiptDownloading(false);
    }
  }, [applicationNumber, context?.transactionId]);

  const handleCta = useCallback(() => {
    const applicationNumberTrim = applicationNumber.trim();
    const targetPath = isSuccess ? "/verification" : "/summary";
    const targetStage = targetPath.replace(/^\//, "") || "summary";
    const productJourneyPage = resolveContinueJourneyPage(
      context?.productJourneyPage,
      productId || context?.productId,
    );
    const origin =
      typeof window !== "undefined" && window.location && window.location.origin
        ? window.location.origin
        : "";
    const encryptedApp =
      encryptJourneyQueryValue(applicationNumberTrim) || applicationNumberTrim;
    const journeyUrl = `${origin}/${productJourneyPage}?applicationNumber=${encodeURIComponent(encryptedApp)}&targetStage=${encodeURIComponent(targetStage)}&fromPaymentReturn=1`;
    const safeJourneyUrl = getSafeRedirectUrl(journeyUrl);
    if (!safeJourneyUrl) {
      setError("Blocked unsafe redirect URL.");
      return;
    }
    if (!isSuccess) {
      try {
        sessionStorage.setItem(REINITIATE_PAYMENT_SUMMARY_SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
    }
    // Full page redirect onto the product journey page; resume runs in that CX.
    window.location.href = safeJourneyUrl;
  }, [context, applicationNumber, isSuccess, productId]);

  if (loading) {
    return (
      <div
        className="quote-premium-loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <BrandLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="payment-return-error"
      >
        {error}
      </div>
    );
  }

  const paidAmount = resolvePaidAmount(context, journeyPaidAmount ?? amount);
  const displayAmount = paidAmount != null ? `Rs ${paidAmount}` : "";

  return (
    <div className="payment-return-page">
      <PaymentModal
        isOpen
        onClose={handleCta}
        title={isSuccess ? "Payment Confirmed" : "Payment Failed"}
        type={isSuccess ? "success" : "error"}
      >
        {isSuccess ? (
          <div className="payment-modal-body">
            <p className="small-body regular">
              Your payment
              {displayAmount ? (
                <>
                  {" "}
                  of amount{" "}
                  <span className="small-body semibold">{displayAmount}</span>
                </>
              ) : null}{" "}
              against the application number{" "}
              <span className="small-body semibold">{applicationNumber}</span>{" "}
              is done. If you want to continue journey click below CTA.
            </p>
            <div className="paymet-modal-btns">
              {context?.transactionId ? (
                <button
                  type="button"
                  className={`modal-btn btn secondary-button regular ${receiptDownloading ? "wait" : "pointer"}`}
                  onClick={handleDownloadReceipt}
                  disabled={receiptDownloading}
                >
                  {receiptDownloading ? "Downloading..." : "Download Receipt"}
                </button>
              ) : null}
              <button
                type="button"
                className={`modal-btn btn primary-button regular`}
                onClick={handleCta}
              >
                Continue
              </button>
              {receiptError ? (
                <span className="payment-receipt-error-text">
                  {receiptError}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="payment-modal-body">
            <p>
              Your payment has been failed. Please reinitiate payment to start
              journey .
            </p>
            <div className="paymet-modal-btns"> 
              <button
                type="button"
                className={`modal-btn btn primary-button regular`}
                onClick={handleCta}
              >
                Reinitiate Payment
              </button>
            </div>
          </div>
        )}
      </PaymentModal>
    </div>
  );
};

export default PaymentReturn;
